/* eslint-disable jsx-a11y/anchor-is-valid */
/* eslint-disable react/no-danger */
/* eslint-disable jam3/no-sanitizer-with-danger */
import React, { ReactElement, useEffect, useState } from 'react';

import { useDispatch } from 'react-redux';
import {
  DragDropContext,
  Droppable,
  Draggable,
  DropResult,
} from 'react-beautiful-dnd';
import { useParams } from 'react-router-dom';
import { Tooltip as ReactTooltip } from 'react-tooltip';

import {
  DeleteIcon,
  EditIcon,
  Container,
  FormContainer,
  Heading,
  Button,
  AddIcon,
  Row,
  Col,
  Paragraph,
  Divider,
  DragIndicatorIcon,
  Dialog,
  Card,
} from 'elmo-elements';

import {
  reOrderOldTooltips,
  sanitizeHTML,
  UpdateEntityPositions,
} from '@/lib/util';
import { ElementType } from '@/modules/types/element';
import {
  useGetElearningQuery,
  useGetFramesQuery,
  useRepositionElementMutation,
  useDeleteElementMutation,
} from '@/modules/services';
import currentFrameDataSelector from '@/modules/selectors/frame';
import { LayoutSectionComponent } from '@/components/LayoutSection';
import { actions as flashMessageActions } from '@/modules/reducers/flashMessage';
import { ElearningParamType } from '../../../routes';

import TooltipModal from './TooltipModal';

import './Tooltips.scss';

const Tooltips = (): ReactElement => {
  const dispatch = useDispatch();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedTooltip, setSelectedTooltip] = useState<ElementType | null>(
    null,
  );

  const { elearningId, frameId } = useParams<ElearningParamType>();
  const elearning = useGetElearningQuery(elearningId);

  const { data: framesData, refetch: refetchFrames } = useGetFramesQuery({
    elearningId,
  });

  const currentFrame = currentFrameDataSelector({
    elearningData: elearning.data,
    frameData: framesData,
    currentFrameId: frameId,
  });
  const [deleteElement, deleteElementStatus] = useDeleteElementMutation();
  const [repositionElement, repositionElementStatus] =
    useRepositionElementMutation();

  const tooltipsColumns = [
    {
      title: 'ID',
    },
    {
      title: 'Name',
    },
    {
      title: 'Type',
    },
  ];

  const tooltipData: ElementType[] =
    (currentFrame && currentFrame.elements) || [];
  const [tooltips, setTooltips] = useState<ElementType[]>(tooltipData);

  useEffect(() => {
    setTooltips(tooltipData);
  }, [tooltipData]);

  // Refetch frames data when frameId changes to ensure we have the latest elements
  useEffect(() => {
    if (frameId) {
      refetchFrames();
    }
  }, [frameId, refetchFrames]);

  useEffect(() => {
    if (repositionElementStatus.isSuccess && repositionElementStatus.data) {
      setTooltips(repositionElementStatus.data);
    }
  }, [repositionElementStatus]);

  useEffect(() => {
    if (repositionElementStatus.isLoading || !repositionElementStatus.data)
      return;

    setTooltips(repositionElementStatus.data);
  }, [repositionElementStatus]);

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedTooltip(null);
  };
  const handleOpenModal = () => {
    setIsModalOpen(true);
  };

  const handleEditTooltip = (tooltip: ElementType) => {
    setSelectedTooltip(tooltip);
    handleOpenModal();
  };

  const handleDeleteTooltip = (tooltipId: string) => {
    Dialog.danger({
      title: 'Confirm Deletion',
      content: 'Are you sure you want to delete this item?',
      ariaLabel: 'Modal windows',
      onOk() {
        deleteElement(tooltipId);
      },
      onCancel() {},
    });
  };

  const handleTooltipUpdated = () => {
    refetchFrames();
  };

  useEffect(() => {
    if (
      deleteElementStatus.isSuccess &&
      !deleteElementStatus.isError &&
      deleteElementStatus.data
    ) {
      dispatch({
        type: flashMessageActions.addFlashMessage,
        payload: {
          isActive: true,
          message: `Tooltip Removed Successfully`,
        },
      });

      handleTooltipUpdated();
    }

    if (deleteElementStatus.isError) {
      dispatch({
        type: flashMessageActions.addFlashMessage,
        payload: {
          isActive: true,
          message: 'Something went wrong',
        },
      });
    }
  }, [deleteElementStatus]);

  const onDragEnd = (result: DropResult) => {
    if (!result.destination) return;

    const sourceIndex = result.source.index;
    const destinationIndex = result.destination.index;

    if (destinationIndex === sourceIndex) return;

    // Check if positions starts with zero or 1
    const isTooltipZeroBased = tooltips.some(
      (tooltip) => tooltip.elementMetadata.position === 0,
    );

    let updatedPositions = UpdateEntityPositions(
      tooltips,
      sourceIndex,
      destinationIndex,
    );

    // Additional logic if the tooltips is migrated from the old course builder which starts from index 1.
    if (!isTooltipZeroBased) {
      updatedPositions = reOrderOldTooltips(
        tooltips,
        sourceIndex,
        destinationIndex,
      );
    }

    if (frameId) {
      repositionElement({
        frameId,
        elementPositions: updatedPositions,
      });
    }
  };

  const totalTooltip =
    tooltips.length > 0
      ? tooltips.reduce(
          (acc, curr) => Math.max(acc, curr.elementMetadata.position),
          0,
        ) + 1
      : tooltips.length;

  return (
    <>
      {isModalOpen && (
        <TooltipModal
          isOpen={isModalOpen}
          totalTooltip={totalTooltip}
          tooltipData={selectedTooltip}
          onClose={handleCloseModal}
          onSave={handleTooltipUpdated}
          frameId={currentFrame && currentFrame.id}
          key={selectedTooltip?.id}
          elearningId={elearning.data?.id}
          tenantId={elearning.data?.tenantId}
        />
      )}
      <div id="Tooltips">
        {!tooltips.length && <Card>No tooltips have been added yet.</Card>}
        <Button
          icon={<AddIcon />}
          onClick={handleOpenModal}
          className="mt-3"
          id="TooltipButtonAdd"
          isUppercase={false}
        >
          Add Tooltip
        </Button>
        <FormContainer maxColumns={2}>
          <Container>
            {!!tooltips.length && (
              <>
                <ReactTooltip
                  id="tooltip-for-tooltip-list"
                  float={true}
                  className="ui-tooltip-content"
                  variant="light"
                  offset={20}
                />
                <Row className="my-3" role="rowheader">
                  <Col span={4} role="columnheader">
                    <Heading size="xs" color="gray" className="pl-3">
                      {tooltipsColumns[0].title}
                    </Heading>
                  </Col>
                  <Col span={10} role="columnheader">
                    <Heading size="xs" color="gray">
                      {tooltipsColumns[1].title}
                    </Heading>
                  </Col>
                  <Col span={4} role="columnheader">
                    <Heading size="xs" color="gray">
                      {tooltipsColumns[2].title}
                    </Heading>
                  </Col>
                </Row>
                <Divider />
                <DragDropContext onDragEnd={onDragEnd}>
                  <Droppable droppableId="droppable">
                    {(provided) => (
                      <div
                        {...provided.droppableProps}
                        ref={provided.innerRef}
                        className={`tooltip-row ${
                          repositionElementStatus.isLoading ? 'cursor-wait' : ''
                        }`}
                      >
                        {tooltips.map((tooltip, index) => (
                          <Draggable
                            key={tooltip.id}
                            draggableId={tooltip.id}
                            index={index}
                            isDragDisabled={repositionElementStatus.isLoading}
                          >
                            {(providedDrag) => (
                              <div
                                ref={providedDrag.innerRef}
                                {...providedDrag.draggableProps}
                              >
                                <Row
                                  key={`Tooltip-${index}`}
                                  className="my-1"
                                  role="row"
                                >
                                  <Col span={4} align="center" role="cell">
                                    <div {...providedDrag.dragHandleProps}>
                                      <DragIndicatorIcon className="drag-icon" />
                                    </div>
                                    <Paragraph color="gray" className="pl-3">
                                      <a
                                        data-tooltip-id="tooltip-for-tooltip-list"
                                        data-tooltip-html={`
                                        <div class="qtip-content">
                                          <div class="tooltip-container">${sanitizeHTML(
                                            tooltip.content,
                                          )}</div>
                                        </div>`}
                                      >
                                        {`tip-${+tooltip.elementMetadata
                                          .position}`}
                                      </a>
                                    </Paragraph>
                                  </Col>
                                  <Col span={10} align="center" role="cell">
                                    <Paragraph color="gray">
                                      {tooltip.name}
                                    </Paragraph>
                                  </Col>
                                  <Col span={4} align="center" role="cell">
                                    <Paragraph color="gray">
                                      {+tooltip.elementMetadata.type === 1
                                        ? 'Inline Tip'
                                        : 'Button Tip'}
                                    </Paragraph>
                                  </Col>
                                  <Col span={6} align="center" role="cell">
                                    <Paragraph align="right">
                                      <Button
                                        icon={<EditIcon />}
                                        onClick={() => {
                                          handleEditTooltip(tooltip);
                                        }}
                                        role="button"
                                        ariaLabel="Edit Tooltip"
                                      />
                                      <Button
                                        icon={<DeleteIcon />}
                                        onClick={() => {
                                          handleDeleteTooltip(tooltip.id);
                                        }}
                                        role="button"
                                        ariaLabel="Delete Tooltip"
                                      />
                                    </Paragraph>
                                  </Col>
                                </Row>
                              </div>
                            )}
                          </Draggable>
                        ))}
                      </div>
                    )}
                  </Droppable>
                </DragDropContext>
              </>
            )}
          </Container>
        </FormContainer>
      </div>
    </>
  );
};

const TooltipsComponent = (): ReactElement => (
  <LayoutSectionComponent
    heading="Tooltips"
    content={<Tooltips />}
    isCollapsible={true}
  />
);

export default TooltipsComponent;
